/**
 * Real Lowe's stores and per-store Pay-By-Scan items.
 *
 * Stores are served as static JSON rather than compiled into the bundle - the
 * full store list would be dead weight on the sign-in screen, and this way
 * refreshing the data is a file swap. Items are no longer static: they come
 * from `/api/items?store=`, scoped to what that store has actually had, so
 * they cannot be cached at build time the way the store list can.
 *
 * Provenance, so nobody has to guess later:
 *  - stores.json: app.Store in sdb-thetaretail-lowes (the store master this
 *    app's own grid renders), LEFT JOINed to defaultData.LowesStores which
 *    stays authoritative for open-vs-closed (ClosingDate IS NULL) and country
 *    (Country = 'USA'); rows with blank city/state are excluded, as are
 *    "UNDEFINED" and jobsite-trailer placeholders - 1,938 stores. A small
 *    provenanced fill in that script supplies city/state for stores the feed
 *    leaves blank (3524, 3617); it applies only where upstream is blank, so it
 *    self-retires rather than masking a real value.
 *    A store present in app.Store with NO LowesStores row is KEPT. Before
 *    2026-08-28 this list came from LowesStores alone, which is missing 28
 *    store numbers that app.Store has; Lowe's of Leland, NC (3478) was one,
 *    unselectable despite 61 live PBS items. Do NOT filter on
 *    app.Store.IsActive/IsOpen (1 on every row, including closed stores) or
 *    app.Store.Country (never populated). The unfiltered "open" set also
 *    contains ~73 still-open Canadian-province rows and a handful of
 *    "0"/blank sentinel rows; both are excluded. Regenerated by
 *    scripts/build-stores.ps1. No coordinates - GPS store detection was
 *    removed, and nothing else uses them.
 *  - items: read from the tenant database per store, scoped to what has
 *    actually shipped to that store this season. See SKU-1566.
 */

export interface Store {
    id: string;
    city: string;
    state: string;
}

export interface PbsItem {
    /** Lowe's item number - what the associate sees on the tag. */
    id: string;
    /** Lowe's description, e.g. "2.00-GAL MUM RED PRM BWL MG PBS". */
    desc: string;
    program: string;
    /** Grower of record for this item at this store; null when nobody owns it. */
    vendorId: number | null;
}

export interface StoreItems {
    /** When the nightly snapshot was built. Shown to the associate. */
    generatedAt: string;
    items: PbsItem[];
}

async function loadJson<T>(
    path: string,
    what: string,
    init: RequestInit = {},
): Promise<T[]> {
    const res = await fetch(path, { ...init, cache: 'no-store' });
    if (!res.ok) throw new Error(`Could not load ${what} (${res.status})`);
    return (await res.json()) as T[];
}

/**
 * The selectable stores, from the snapshot that also carries the pick lists.
 *
 * Served behind the session (the picker only renders after sign-in) so the list
 * and the items an associate can report come from one build at one moment. The
 * server falls back to the static file for a snapshot built before the list was
 * folded in, so this call works either way.
 */
export const loadStores = () =>
    loadJson<Store>('/api/stores', 'stores', { credentials: 'same-origin' });

/**
 * The items this store has actually had. Not a catalogue - a store cannot
 * report a plant it never had, so anything absent here is absent on purpose.
 */
export async function loadStoreItems(storeId: string): Promise<StoreItems> {
    const res = await fetch(`/api/items?store=${encodeURIComponent(storeId)}`, {
        credentials: 'same-origin',
        cache: 'no-store',
    });
    if (!res.ok) throw new Error(`Could not load items for store ${storeId} (${res.status})`);
    return (await res.json()) as StoreItems;
}

export function storeLabel(store: Store): string {
    return `${store.id} - ${store.city}, ${store.state}`;
}

/**
 * Find a store by number or by place.
 *
 * Number first and number only when the query looks like one: an associate
 * almost always knows their store number, and mixing the two searches lets a
 * digit inside a number match a city query. Same class of bug as the item
 * search, which once returned a 1.00-GAL item for "2 gal red".
 */
export function searchStores(stores: Store[], query: string, limit = 12): Store[] {
    const trimmed = query.trim().toLowerCase();
    if (trimmed === '') return [];

    if (/^\d+$/.test(trimmed)) {
        const exact: Store[] = [];
        const prefix: Store[] = [];
        for (const store of stores) {
            if (store.id === trimmed) exact.push(store);
            else if (store.id.startsWith(trimmed)) prefix.push(store);
        }
        return [...exact, ...prefix].slice(0, limit);
    }

    const terms = trimmed.split(/\s+/).filter(Boolean);
    const out: Store[] = [];
    for (const store of stores) {
        const hay = `${store.city} ${store.state}`.toLowerCase();
        if (terms.every((t) => hay.includes(t))) {
            out.push(store);
            if (out.length >= limit) break;
        }
    }
    return out;
}

/**
 * Match items by item number, or by every word appearing in the description.
 *
 * Two rules, both learned the hard way with 678 near-identically-named mums:
 *
 * 1. Every term must match, not any. An OR search returns half the catalogue.
 * 2. Item numbers are only searched when the whole query looks like one. Search
 *    both at once and "2 gal red" returns a 1.00-GAL item, because the "2"
 *    matched inside an item number - the size in the query is silently ignored,
 *    which is exactly the sort of near-miss that gets the wrong plant reported.
 */
export function searchItems(items: PbsItem[], query: string, limit = 12): PbsItem[] {
    const trimmed = query.trim().toLowerCase();
    if (trimmed === '') return [];

    const out: PbsItem[] = [];

    if (/^\d{3,}$/.test(trimmed)) {
        for (const item of items) {
            if (item.id.includes(trimmed)) {
                out.push(item);
                if (out.length >= limit) break;
            }
        }
        return out;
    }

    const terms = trimmed.split(/\s+/).filter(Boolean);
    for (const item of items) {
        const desc = item.desc.toLowerCase();
        if (terms.every((t) => desc.includes(t))) {
            out.push(item);
            if (out.length >= limit) break;
        }
    }
    return out;
}

/*
 * The disclaimer that used to live here is gone.
 *
 * It explained the pick list's filtering rules and the snapshot date on every
 * screen. Associates do not need the rules restated while they are holding a
 * plant, and the empty state already explains itself in its own words. The
 * six-month window in scripts/snapshot.sql is now the only place that logic
 * is expressed - keep it documented there, not in the UI.
 */
